import { useEffect, useState } from 'react';
import {
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
  Button,
  makeStyles,
} from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { useIsEcosoftStaff } from '../stores/authStore.js';
import { usePricingStore } from '../stores/pricingStore.js';

const useStyles = makeStyles({
  wrap: {
    padding: '0 var(--space-xxl) var(--space-m)',
  },
});

/**
 * Banner SOLO para staff de Ecosoft. Avisa cuando el chequeo diario de tarifas
 * LLM (job `check-llm-pricing`) actualizó un precio (info) o no pudo verificar y
 * requiere revisión (warning). El cliente nunca lo ve (gate useIsEcosoftStaff).
 * Email diferido — por ahora la notificación es este banner in-app.
 */
export function PricingAlertBanner() {
  const styles = useStyles();
  const isStaff = useIsEcosoftStaff();
  const data = usePricingStore((s) => s.data);
  const ensureLoaded = usePricingStore((s) => s.ensureLoaded);
  const [dismissed, setDismissed] = useState<string | null>(null);

  useEffect(() => {
    if (isStaff) void ensureLoaded();
  }, [isStaff, ensureLoaded]);

  if (!isStaff) return null;
  const alert = data?.alert;
  if (!alert || !alert.kind) return null;

  // Clave de dismiss por evento (kind + fecha) → reaparece si hay uno nuevo.
  const key = `${alert.kind}:${alert.at ?? ''}`;
  if (dismissed === key) return null;

  const isReview = alert.kind === 'needs_review';
  const title = isReview
    ? 'Revisa el chequeo de tarifas LLM'
    : 'Se actualizó la tarifa de un modelo LLM';
  // Fecha del evento: needs_review → cuándo se hizo la revisión automática;
  // updated → cuándo se aplicó el cambio de precio.
  const when = alert.at
    ? new Date(alert.at).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' })
    : null;
  const body = isReview
    ? `El chequeo automático de precios no pudo verificar la tarifa y no la cambió${when ? ` (revisado el ${when})` : ''}. ${alert.note ?? ''}`
    : `El chequeo diario actualizó el precio automáticamente${when ? ` el ${when}` : ''}. ${alert.note ?? ''}`;

  return (
    <div className={styles.wrap}>
      <MessageBar intent={isReview ? 'warning' : 'info'} layout="multiline">
        <MessageBarBody>
          <MessageBarTitle>{title}</MessageBarTitle> {body}
        </MessageBarBody>
        <MessageBarActions
          containerAction={
            <Button
              appearance="transparent"
              icon={<Dismiss20Regular />}
              aria-label="Cerrar aviso"
              onClick={() => setDismissed(key)}
            />
          }
        />
      </MessageBar>
    </div>
  );
}
